/**
 * A single team: who leads it, where it sits in the hierarchy, and its
 * roster. No prototype counterpart — see `TeamsPage.jsx`'s header note.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import {
  Card, Badge, Avatar, EmptyState, ErrorState,
} from '../../components/common/index.jsx';
import { Spinner } from '../../components/common/Loading.jsx';
import {
  useTeam, useTeamMembers, useUserPickerOptions, useAddTeamMembers,
  useRemoveTeamMember, useDeleteTeam,
} from './useTeams.js';
import { toRow, toMemberRow } from './teamShape.js';
import { AddMembersModal, DeleteTeamModal } from './TeamModals.jsx';

export default function TeamDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('teams', 'edit');
  const canDelete = can('teams', 'delete');

  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data: team, isLoading, error, refetch } = useTeam(id);
  const { data: membersData, isLoading: membersLoading } = useTeamMembers(id, {}, Boolean(team));
  const userOptions = useUserPickerOptions();

  const addMembers = useAddTeamMembers();
  const removeMember = useRemoveTeamMember();
  const deleteTeam = useDeleteTeam();

  if (isLoading) return <Spinner label="Loading team" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!team) return null;

  const row = toRow(team);
  const members = (membersData?.data ?? []).map(toMemberRow);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb">
            <a href="/teams" onClick={(e) => { e.preventDefault(); navigate('/teams'); }}>My Team</a>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" />
            <span>{row.name}</span>
          </div>
          <h1 className="page-title">{row.name}</h1>
          <p className="page-sub">
            <code>{row.path}</code>
            {row.parent && <> · nests under <a href={`/teams/${row.parent.id}`} onClick={(e) => { e.preventDefault(); navigate(`/teams/${row.parent.id}`); }}>{row.parent.name}</a></>}
          </p>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          {canEdit && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => setAddOpen(true)}>
              <i className="bi bi-person-plus me-1" aria-hidden="true" />Add members
            </button>
          )}
          {canDelete && (
            <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setDeleting(true)}>
              <i className="bi bi-trash me-1" aria-hidden="true" />Delete team
            </button>
          )}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <Card title="Team">
            <dl className="detail-grid mb-0">
              <div className="detail-row"><dt>Code</dt><dd>{row.code}</dd></div>
              <div className="detail-row"><dt>Lead</dt><dd>{row.manager?.full_name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Business unit</dt><dd>{row.businessUnit ?? '—'}</dd></div>
              <div className="detail-row"><dt>Status</dt><dd><Badge tone={row.statusTone}>{row.statusLabel}</Badge></dd></div>
              <div className="detail-row"><dt>Members</dt><dd>{row.memberCount}</dd></div>
            </dl>
          </Card>
        </div>
        <div className="col-md-8">
          <Card title="Roster">
            {membersLoading ? <Spinner label="Loading roster" /> : members.length === 0 ? (
              <EmptyState icon="bi-people" title="No members yet" message="Add people to this team with the button above." />
            ) : (
              <ul className="list-x">
                {members.map((m) => (
                  <li key={m.id} className="list-x-item">
                    <Avatar name={m.fullName} size={30} />
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-600 t-sm text-truncate">{m.fullName}</div>
                      <div className="t-2xs ink-4 text-truncate">{m.email}</div>
                    </div>
                    {m.isLead && <Badge tone="sky">Lead</Badge>}
                    {canEdit && !m.isLead && (
                      <button
                        type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }}
                        title="Remove from team" disabled={removeMember.isPending}
                        onClick={() => removeMember.mutate({ teamId: id, userId: m.id })}
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <AddMembersModal
        open={addOpen}
        userOptions={userOptions}
        existingMemberIds={members.map((m) => m.id)}
        busy={addMembers.isPending}
        onClose={() => setAddOpen(false)}
        onSubmit={async (userIds) => {
          await addMembers.mutateAsync({ id, userIds });
          setAddOpen(false);
        }}
      />

      <DeleteTeamModal
        open={deleting}
        team={row}
        busy={deleteTeam.isPending}
        onClose={() => setDeleting(false)}
        onConfirm={async (reason) => {
          await deleteTeam.mutateAsync({ id, reason, rowVersion: row.rowVersion });
          setDeleting(false);
          navigate('/teams');
        }}
      />
    </>
  );
}
